import React, { useState, useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import "./AudioPlayer.css";

const AudioPlayer = ({ specUrl, src, title, artist }) => {
  const audioRef = useRef(null);
  const rafRef = useRef(0);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [paused, setPaused] = useState(true);
  const [volume, setVolume] = useState(0.5);

  const format = (time) => {
    if (isNaN(time)) return "...";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setTime(audio.currentTime);
    const handleLoadedMetadata = () => setDuration(audio.duration);
    const handleEnded = () => setTime(0);

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("ended", handleEnded);
    };
  }, []);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  const handlePlayPause = () => {
    const audio = audioRef.current;
    if (paused) {
      audio.play();
    } else {
      audio.pause();
    }
    setPaused(!paused);
  };

  const handleSeek = (e) => {
    const div = e.currentTarget;
    const seek = (e) => {
      const { left, width } = div.getBoundingClientRect();
      let p = (e.clientX - left) / width;
      p = Math.max(0, Math.min(1, p));
      const newTime = p * duration;
      if (audioRef.current) {
        audioRef.current.currentTime = newTime;
        setTime(newTime);
      }
    };

    seek(e);

    const handlePointerMove = (e) => seek(e);
    const handlePointerUp = () => {
      window.removeEventListener("pointermove", handlePointerMove);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp, { once: true });
  };

  return (
    <div className="audio-container">
      <div className="spectrogram">
        <div
          className="spectrogram-pointer"
          style={{
            left: `calc(-1vw + ${(time / duration) * 100}%)`,
            transform: `translateX(${paused ? "0" : "2px"}) translateZ(0)`,
            color: "white"
          }}
        />
        <img className="spectrogram-image" src={specUrl} alt="" />
      </div>

      <div className={`player ${paused ? "paused" : ""}`}>
        <audio ref={audioRef} src={src} preload="metadata" />

        <button
          className="play"
          aria-label={paused ? "play" : "pause"}
          onClick={handlePlayPause}
        />

        <div className="info">
          <div className="description">
            <strong>{title}</strong>
            {artist && (
              <>
                {" / "}
                <span>{artist}</span>
              </>
            )}
          </div>

          <div className="progress-bar">
            <span>{format(time)}</span>
            <div className="slider" onPointerDown={handleSeek}>
              <div
                className="progress"
                style={{ width: `${(time / duration) * 100}%` }}
              />
            </div>
            <span>{duration ? format(duration) : "--:--"}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

AudioPlayer.propTypes = {
  specUrl: PropTypes.string,
  src: PropTypes.string.isRequired,
  title: PropTypes.string,
  artist: PropTypes.string,
};

export default AudioPlayer;
